import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import {
  approveDocument,
  getPendingDocuments,
  PendingDocument,
  rejectDocument,
  rejectDocumentsBatch,
} from "../../lib/api/Documents";

export function AdminApprovals() {
  const { userInfo } = useUserInfoContext();
  const [documents, setDocuments] = useState<PendingDocument[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectingAll, setRejectingAll] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadPendingDocuments = useCallback(async (silent = false) => {
    if (!userInfo?.accessToken) {
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const response = await getPendingDocuments(userInfo.accessToken);
      if (response?.data) {
        setDocuments(response.data.documents || []);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "Failed to load pending documents. Admin access required."
      );
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [userInfo?.accessToken]);

  useEffect(() => {
    loadPendingDocuments();
  }, [loadPendingDocuments]);

  useEffect(() => {
    const hasIngesting = documents.some((doc) => doc.status === "ingesting");
    if (!hasIngesting) {
      return;
    }

    const interval = window.setInterval(() => {
      loadPendingDocuments(true);
    }, 15000);

    return () => window.clearInterval(interval);
  }, [documents, loadPendingDocuments]);

  const handleApprove = async (documentId: string) => {
    if (!userInfo?.accessToken) {
      return;
    }

    try {
      setApprovingId(documentId);
      setSuccessMessage(null);
      await approveDocument(documentId, userInfo.accessToken);
      setSuccessMessage(`Document ${documentId} approved and migrated to main KB.`);
      await loadPendingDocuments();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          `Failed to approve document ${documentId}.`
      );
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (documentId: string) => {
    if (!userInfo?.accessToken) {
      return;
    }

    try {
      setRejectingId(documentId);
      setSuccessMessage(null);
      await rejectDocument(documentId, userInfo.accessToken);
      setSuccessMessage(
        `Document ${documentId} rejected. It stays in the user's temporary KB but will not be migrated to the main KB.`
      );
      await loadPendingDocuments();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          `Failed to reject document ${documentId}.`
      );
    } finally {
      setRejectingId(null);
    }
  };

  const handleRejectAll = async () => {
    if (!userInfo?.accessToken || documents.length === 0) {
      return;
    }

    const confirmed = window.confirm(
      `Reject all ${documents.length} pending document(s)? They will remain in each user's temporary KB but will not be migrated to the main KB.`
    );
    if (!confirmed) {
      return;
    }

    try {
      setRejectingAll(true);
      setSuccessMessage(null);
      const documentIds = documents.map((doc) => doc.documentId);
      const result = await rejectDocumentsBatch(documentIds, userInfo.accessToken);
      const rejectedCount = result?.data?.rejectedCount ?? documentIds.length;
      setSuccessMessage(`Rejected ${rejectedCount} document(s).`);
      await loadPendingDocuments();
    } catch (err: any) {
      setError(
        err?.response?.data?.message || "Failed to reject the document batch."
      );
    } finally {
      setRejectingAll(false);
    }
  };

  const actionInProgress =
    approvingId !== null || rejectingId !== null || rejectingAll;

  return (
    <Box sx={{ p: 4, maxWidth: 1100, mx: "auto" }}>
      <Typography variant="h4" gutterBottom>
        Document Approvals
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Review user-uploaded documents in the temporary knowledge base and
        approve them for ingestion into the main knowledge base.
      </Typography>

      <Paper sx={{ p: 2, mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="h6">Pending documents:</Typography>
        <Chip label={totalCount} color="primary" />
        <Button variant="outlined" onClick={() => loadPendingDocuments()} disabled={loading}>
          Refresh
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          color="error"
          onClick={handleRejectAll}
          disabled={loading || documents.length === 0 || actionInProgress}
        >
          {rejectingAll ? "Rejecting..." : "Reject All"}
        </Button>
      </Paper>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {!loading && documents.length === 0 && (
        <Alert severity="info">No documents awaiting approval.</Alert>
      )}

      {!loading && documents.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Filename</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>Access Level</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Uploaded At</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={`${doc.userId}-${doc.documentId}`}>
                  <TableCell>{doc.filename}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {doc.userId}
                  </TableCell>
                  <TableCell>{doc.accessLevel}</TableCell>
                  <TableCell>
                    <Chip
                      label={doc.status}
                      size="small"
                      color={doc.status === "pending" ? "warning" : "info"}
                    />
                  </TableCell>
                  <TableCell>{doc.uploadedAt}</TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        disabled={actionInProgress}
                        onClick={() => handleApprove(doc.documentId)}
                      >
                        {approvingId === doc.documentId
                          ? "Approving..."
                          : "Approve"}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={actionInProgress}
                        onClick={() => handleReject(doc.documentId)}
                      >
                        {rejectingId === doc.documentId
                          ? "Rejecting..."
                          : "Reject"}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
